import { KartoyIcon } from '@mobile/components/kartoy/kartoy-icon'
import { GTextAnimated } from '@shared-ui/components/ui/text'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { useAppTheme } from '@shared-ui/theme'
import { rf, rh, rw } from '@shared-ui/theme/scale'
import { memo, useEffect, useLayoutEffect, useRef } from 'react'
import { Platform, Pressable, type PressableProps } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { useCreateStyles } from './styles'

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  [',', '0', 'backspace'],
] as const

export type NumpadKey = (typeof KEYS)[number][number]

type KeyContentProps = {
  value: NumpadKey
}

// Содержимое клавиши: иконка backspace или символ.
function KeyContent({ value }: KeyContentProps) {
  const styles = useCreateStyles()
  const theme = useAppTheme()

  if (value === 'backspace') {
    return <KartoyIcon color={theme.COLOR_TEXT_PRIMARY} icon="backspace" size={28} />
  }

  return (
    <GTextAnimated
      fw={600}
      selectable={false}
      style={[styles.keyLabel, { fontSize: rf(24), letterSpacing: rw(-0.24), lineHeight: rh(31) }]}
    >
      {value}
    </GTextAnimated>
  )
}

type NumpadProps = {
  onPressKey: (key: NumpadKey) => void
  rowGap?: number
  isDecimalKeyVisible?: boolean
  onKeyPress?: () => void
}

// Клавиша: 120×56 pill, текст 24 SemiBold; press — scale + вспышка фона.
const NumpadKeyButton = memo(function NumpadKeyButtonComponent({
  value,
  onPressKey,
  rowGap,
  isVisible = true,
  onKeyPress,
}: {
  value: NumpadKey
  onPressKey: (key: NumpadKey) => void
  rowGap: number
  isVisible?: boolean
  onKeyPress?: () => void
}) {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const press = useSharedValue(0)
  const haptic = useHaptic()
  const isTouchHandledRef = useRef(false)
  const isRepeatingRef = useRef(false)
  const repeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const animatedStyle = useAnimatedStyle(() => ({
    backgroundColor: press.value > 0.5 ? theme.COLOR_BG_FILL : 'transparent',
    transform: [{ scale: 1 - press.value * 0.08 }],
  }))

  const handlePress = () => {
    if (!isVisible) return
    haptic('impactLight')
    onPressKey(value)
    onKeyPress?.()
  }

  const latestPressRef = useRef(handlePress)
  useLayoutEffect(() => {
    latestPressRef.current = handlePress
  })

  const stopRepeating = () => {
    if (repeatTimerRef.current !== null) {
      clearInterval(repeatTimerRef.current)
      repeatTimerRef.current = null
    }
  }

  useEffect(() => stopRepeating, [])

  const handleLongPress = () => {
    if (value !== 'backspace' || !isVisible) return

    stopRepeating()
    isRepeatingRef.current = true
    latestPressRef.current()
    // Каждый повтор использует актуальное значение поля после предыдущего удаления.
    repeatTimerRef.current = setInterval(() => latestPressRef.current(), 80)
  }

  // На web вводим при касании, не дожидаясь отпускания пальца и браузерного click.
  const handlePointerDown: PressableProps['onPointerDown'] = (event) => {
    isTouchHandledRef.current = event.nativeEvent.pointerType === 'touch'

    if (isTouchHandledRef.current) handlePress()
  }

  const handleWebPress: PressableProps['onPress'] = (event) => {
    const nativeEvent = event.nativeEvent
    const isTouchClick = 'pointerType' in nativeEvent && nativeEvent.pointerType === 'touch'
    // detail === 0 — активация с клавиатуры или через средства доступности.
    const isCompatibilityClick = isTouchHandledRef.current && 'detail' in nativeEvent && nativeEvent.detail !== 0

    isTouchHandledRef.current = false

    if (!isTouchClick && !isCompatibilityClick && !isRepeatingRef.current) handlePress()
  }

  return (
    <AnimatedPressable
      disabled={!isVisible}
      onPointerDown={Platform.OS === 'web' ? handlePointerDown : undefined}
      onPress={Platform.OS === 'web' ? handleWebPress : () => !isRepeatingRef.current && handlePress()}
      delayLongPress={350}
      onLongPress={value === 'backspace' ? handleLongPress : undefined}
      onPointerUp={stopRepeating}
      onPointerCancel={stopRepeating}
      onPressIn={() => {
        isRepeatingRef.current = false
        press.value = withTiming(1, { duration: 80 })
      }}
      onPressOut={() => {
        stopRepeating()
        press.value = withTiming(0, { duration: 140 })
      }}
      style={[styles.key, animatedStyle, { marginTop: rowGap }]}
    >
      {isVisible && <KeyContent value={value} />}
    </AnimatedPressable>
  )
})

// Кастомный numpad макета kartoy: 1-9, запятая, 0, backspace.
export const Numpad = memo(function NumpadComponent({
  onPressKey,
  rowGap = 0,
  isDecimalKeyVisible = true,
  onKeyPress,
}: NumpadProps) {
  const styles = useCreateStyles()

  return (
    <>
      {KEYS.map((row, rowIndex) => (
        <Animated.View key={row.join('-')} style={[styles.row, rowIndex > 0 && { marginTop: rh(rowGap) }]}>
          {row.map((value) => (
            <NumpadKeyButton
              key={value}
              value={value}
              onPressKey={onPressKey}
              onKeyPress={onKeyPress}
              isVisible={value !== ',' || isDecimalKeyVisible}
              rowGap={0}
            />
          ))}
        </Animated.View>
      ))}
    </>
  )
})
